import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import {
  Package,
  ShoppingCartState,
} from '../../../shopping-cart-back/actions/standalone';
import { ZalamoDialogService } from '../../../../zalamo-dialog/zalamo-dialog.service';
import { select, Store } from '@ngrx/store';
import { PrintDetailsComponent } from '../product-details/print-details/print-details.component';
import {
  selectFreePackage,
  selectPackagesInBasket,
  selectPackagesInShop,
  selectProductsList,
  selectShoppingCartContext,
} from '../../../shopping-cart-back/actions/selector';
import { ViewProductInPackageComponent } from '../view-product-in-package/view-product-in-package.component';
import { first, map, takeUntil } from 'rxjs/operators';
import { combineLatest } from 'rxjs';
import { ZalamoObject } from '../../../../../zalamo/pipes/names/graphql';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { SuggestPackageAtBeginningService } from 'ZalamoLibrary/src/lib/ui/products/products-front/components/view-package/suggest-package-at-beginning.service';
import { PhotoPreviewComponent } from 'ZalamoLibrary/src/lib/ui/products/products-front/components/photo-preview/photo-preview.component';
import { translationProvider } from '@ui/nls/translations/translate.directive';

@Component({
    selector: 'lib-view-package',
    templateUrl: './view-package.component.html',
    styleUrls: ['./view-package.component.scss'],
    providers: [translationProvider('productFront')],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ViewPackageComponent implements OnInit {
  context = ZalamoObject.PublicSession;
  productsList$ = this.store.pipe(select(selectProductsList));
  packagesInShop$ = this.store.pipe(select(selectPackagesInShop));
  packagesInBasket$ = this.store.pipe(select(selectPackagesInBasket));
  shoppingCartContext$ = this.store.pipe(select(selectShoppingCartContext));
  freePackage$ = this.store.pipe(select(selectFreePackage));
  @Input() package: Package;
  @Input() quantity = 1;
  @Output() addPacketToBasket = new EventEmitter();
  @Output() hideDialog = new EventEmitter();
  arePrints = false;
  areFiles = false;
  freePackage = false;

  oneImageInShop$ = combineLatest([this.packagesInShop$]).pipe(
    map(() => {
      return this.findFilesAndPrintsInPackage('OneImage');
    })
  );
  anyImageInShop$ = combineLatest([this.packagesInShop$]).pipe(
    map(([packagesInShop]) => {
      return this.findFilesAndPrintsInPackage('AnyImage').map(
        (filesAndPrints) => ({
          ...filesAndPrints,
          addFileToPrintInPackage: packagesInShop.package
            .find(({ packageId }) => packageId === this.package.packageId)
            .printAndFileConfig.find(
              ({ printAndFileConfigId }) =>
                printAndFileConfigId === filesAndPrints.printAndFileConfigId
            ).addFileToPrintInPackage,
        })
      );
    })
  );
  pickedImageInShop$ = combineLatest([this.packagesInShop$]).pipe(
    map(() => {
      return this.findFilesAndPrintsInPackage('PickedImage');
    })
  );
  isPhonePortrait$ = this.screenObserver
    .observe([Breakpoints.HandsetPortrait, Breakpoints.HandsetLandscape])
    .pipe(map(({ matches }) => matches));

  constructor(
    private screenObserver: BreakpointObserver,
    private dialogService: ZalamoDialogService,
    private store: Store<{ shoppingCart: ShoppingCartState }>,
    private suggestFreePackage: SuggestPackageAtBeginningService
  ) {}

  ngOnInit(): void {
    this.package.printAndFileConfig.forEach((value) => {
      if (value.printsCount.length) {
        this.arePrints = true;
      }
      if (value.filesConfig.length) {
        this.areFiles = true;
      }
    });
    combineLatest([this.freePackage$, this.packagesInBasket$])
      .pipe(first())
      .subscribe(([freePackage, packagesInBasket]) => {
        if (
          !this.suggestFreePackage.loadAlreadyOrdered() &&
          this.package.packageId === freePackage.freePackageId &&
          !packagesInBasket.find(
            ({ packageId }) => packageId === freePackage.freePackageId
          )
        ) {
          this.freePackage = true;
        }
      });
  }

  findFilesAndPrintsInPackage(currentMode: string) {
    const fileAndPrint: Array<{
      printAndFileConfigId: string;
      groups: Array<string>;
      forcedImage: { photoId: string; photoGroupId: string };
      print: Array<{}>;
      file: Array<{}>;
    }> = [];
    this.package.printAndFileConfig.forEach(
      ({
        printAndFileConfigId,
        filesConfig,
        groups,
        printsCount,
        mode,
        forcedImage,
      }) => {
        if (mode === currentMode) {
          fileAndPrint.push({
            printAndFileConfigId,
            forcedImage,
            print: [],
            file: [],
            groups,
          });
          filesConfig.forEach((f) => {
            fileAndPrint
              .find(
                (fAndP) => fAndP.printAndFileConfigId === printAndFileConfigId
              )
              .file.push({ ...f });
          });
          printsCount.forEach((p) => {
            fileAndPrint
              .find(
                (fAndP) => fAndP.printAndFileConfigId === printAndFileConfigId
              )
              .print.push({ ...p });
          });
        }
      }
    );
    return fileAndPrint;
  }

  showPrintDetails(printFormatId, printPaperId) {
    const ref = this.dialogService.openRight(PrintDetailsComponent);
    const { componentInstance } = ref;
    componentInstance.printPaperId = printPaperId;
    componentInstance.printFormatId = printFormatId;
  }

  openFullPhoto(photoId: string) {
    const ref = this.dialogService.openFull(PhotoPreviewComponent);
    this.isPhonePortrait$
      .pipe(takeUntil(ref.afterClosed()))
      .subscribe((isPhonePortrait) => {
        ref._containerInstance.isBare = isPhonePortrait;
      });
    ref.componentInstance.photoId = photoId;
  }

  openInformationOnProduct(productInPackageId: string, productId: string) {
    const ref = this.dialogService.openFull(ViewProductInPackageComponent);
    this.isPhonePortrait$
      .pipe(takeUntil(ref.afterClosed()))
      .subscribe((isPhonePortrait) => {
        ref._containerInstance.isBare = isPhonePortrait;
      });
    ref.componentInstance.packageId = this.package.packageId;
    ref.componentInstance.productInPackage = this.package.productConfig.find(
      (value) => value.productInPackageId === productInPackageId
    );
    this.productsList$
      .pipe(first())
      .subscribe(
        (value1) =>
          (ref.componentInstance.productInShop = value1.find(
            (value2) => value2.productId === productId
          ))
      );
    ref.componentInstance.hideDialog
      .pipe(takeUntil(ref.afterClosed()))
      .subscribe(() => {
        ref.close();
      });
  }
}
